"use client";

import { useState, useMemo, useEffect } from "react";
import { Phone, MessageSquare, Mail, Mic } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/app/_module/components/ui/tabs";
import { Button } from "@/app/_module/components/ui/button";
import { DataPagination } from "@/app/_module/components/ui/data-pagination";
import type {
  DirectionFilter,
  StatusFilter,
  SMSStatusFilter,
  CallRecord,
} from "@/app/_module/types/communication";

import { CallList } from "./_modules/components/CallList";
import { CommunicationFilters, type EmailStatusFilter } from "./_modules/components/CommunicationFilters";
import { SMSList } from "./_modules/components/SMSList";
import { EmailList } from "./_modules/components/EmailList";
import { VoicemailTab } from "./_modules/components/VoicemailTab";
import { EmailDetailModal } from "../leads/_modules/components/EmailDetailModal";
import { CallsQueryParams, transformCallData } from "./_modules/services/communicationService";
import type { DurationFilter } from "./_modules/types/communication-history.types";
import { toDisplayCallRecord, toDisplaySmsRecord } from "./_modules/utils/transform-communication-data";
import { useGetLeads } from "../leads/_modules/hooks/queries";
import { useCalls, useSMS, useEmails, useVoicemails, useMarkVoicemailAsListened, useDeleteVoicemail } from "./_modules/hooks/use-hook";
import type { VoicemailRecord } from "./_modules/types/voicemail";
import type { EmailHistory } from "./_modules/types/email-history.types";
import { toast } from "@/app/_module/hooks/use-toast";
import { getProxyRecordingUrl } from "@/app/_module/lib/utils";

const Communication = function () {
  const router = useRouter();
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();

  // Normalize a ?tab= value to a real TabsTrigger value. Deep-links from other
  // surfaces (e.g. the dashboard Recent Communication box) historically used
  // short aliases like 'sms' / 'calls' that never matched the actual tab values
  // ('sms-history' / 'call-history'), so the tab silently didn't switch.
  const normalizeTab = (raw?: string | null): string => {
    const aliases: Record<string, string> = {
      sms: 'sms-history',
      'sms-history': 'sms-history',
      calls: 'call-history',
      call: 'call-history',
      'call-history': 'call-history',
      email: 'email-history',
      emails: 'email-history',
      'email-history': 'email-history',
      voicemail: 'voicemail',
      voicemails: 'voicemail',
    };
    return (raw && aliases[raw]) || 'call-history';
  };

  // Get initial tab from URL or default to "call-history"
  const [activeTab, setActiveTab] = useState(() => {
    return normalizeTab(searchParams.get('tab'));
  });

  // Handle tab change - update URL to persist tab selection
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    const params = new URLSearchParams(searchParams.toString());
    params.set('tab', value);
    router.replace(`?${params.toString()}`, { scroll: false });
  };

  // Sync tab state with URL on initial load and URL changes
  useEffect(() => {
    const tabFromUrl = normalizeTab(searchParams.get('tab'));
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Mark missed calls as read + save SMS last-seen timestamp when visiting this page
  useEffect(() => {
    // Save "now" as last SMS seen time
    localStorage.setItem('comm-sms-last-seen', new Date().toISOString());
    // Mark missed calls as read on the backend, then invalidate sidebar badge
    (async () => {
      try {
        const { getApiUrl } = await import("@/app/_module/services/config");
        const api = (await import("@/app/_module/services/api")).default;
        await api.post({ url: getApiUrl("communication/counts/mark-read"), auth: true });
        // Clear the sidebar unread badge immediately
        queryClient.invalidateQueries({ queryKey: ['communication-unread-total'] });
      } catch {}
    })();
  }, [queryClient]);

  // Auto-refresh emails when email tab is active (refetch on tab switch)
  useEffect(() => {
    if (activeTab === 'email-history') {
      // Immediately refetch when switching to email tab
      refetchEmails();
    }
  }, [activeTab]);

  const [searchQuery, setSearchQuery] = useState("");
  const [callDirectionFilter, setCallDirectionFilter] =
    useState<DirectionFilter>("all");
  const [smsDirectionFilter, setSmsDirectionFilter] =
    useState<DirectionFilter>("all");
  const [emailDirectionFilter, setEmailDirectionFilter] =
    useState<DirectionFilter>("all");
  const [callStatusFilter, setCallStatusFilter] = useState<StatusFilter>("all");
  const [smsStatusFilter, setSmsStatusFilter] =
    useState<SMSStatusFilter>("all");
  const [emailStatusFilter, setEmailStatusFilter] =
    useState<EmailStatusFilter>("all");
  const [callDurationFilter, setCallDurationFilter] =
    useState<DurationFilter>("all");

  // Email detail modal state
  const [selectedEmail, setSelectedEmail] = useState<EmailHistory | null>(null);
  const [isEmailDetailOpen, setIsEmailDetailOpen] = useState(false);

  // Handle email click - open detail modal
  const handleEmailClick = (email: EmailHistory) => {
    setSelectedEmail(email);
    setIsEmailDetailOpen(true);
  };

  // Pagination state
  const [callPage, setCallPage] = useState(1);
  const [callPageSize, setCallPageSize] = useState(25);
  const [smsPage, setSmsPage] = useState(1);
  const [smsPageSize, setSmsPageSize] = useState(25);

  // Build query parameters for calls API — server-side filtering
  const callParams: CallsQueryParams = useMemo(() => {
    const params: CallsQueryParams = {};

    if (searchQuery) {
      params.search = searchQuery;
    }

    // Server-side filters for efficient querying
    if (callStatusFilter !== 'all') {
      // DB stores `no_answer` (underscore); frontend filter value is `no-answer` (hyphen)
      (params as any).callStatus = callStatusFilter === 'no-answer' ? 'no_answer' : callStatusFilter;
    }
    if (callDirectionFilter !== 'all') {
      (params as any).direction = callDirectionFilter;
    }

    params.page = callPage;
    params.limit = callPageSize;

    return params;
  }, [searchQuery, callPage, callPageSize, callStatusFilter, callDirectionFilter]);

  // Build query parameters for SMS API — server-side filtering
  const smsParams = useMemo(() => ({
    page: smsPage,
    limit: smsPageSize,
    ...(smsDirectionFilter !== 'all' && { direction: String(smsDirectionFilter) }),
    ...(smsStatusFilter !== 'all' && { status: String(smsStatusFilter) }),
  }), [smsPage, smsPageSize, smsStatusFilter, smsDirectionFilter]);

  // Use React Query hooks instead of manual state management
  const { data: callsData, isLoading: isLoadingCalls, error: callsError, refetch: refetchCalls } = useCalls(callParams, {
    placeholderData: (previousData: any) => previousData, // Keep previous data while loading
  });
  const { data: smsData, isLoading: isLoadingSms, error: smsError, refetch: refetchSMS } = useSMS(smsParams, {
    placeholderData: (previousData: any) => previousData,
  });
  const { data: emailsData, isLoading: isLoadingEmails, error: emailsError, refetch: refetchEmails } = useEmails({
    status: emailStatusFilter !== "all" ? emailStatusFilter : undefined,
    type: emailDirectionFilter !== "all" ? emailDirectionFilter : undefined,
    search: searchQuery || undefined,
  });
  const { data: voicemailsData } = useVoicemails();

  // Voicemail mutations
  const markAsListenedMutation = useMarkVoicemailAsListened();
  const deleteVoicemailMutation = useDeleteVoicemail();

  // Transform calls data with proper display formatting
  const calls: CallRecord[] = useMemo(() => {
    if (!callsData?.items || !Array.isArray(callsData.items)) {
      return [];
    }
    return callsData.items.map((item: any) => {
      const transformed = transformCallData(item);
      return toDisplayCallRecord(transformed) as CallRecord;
    });
  }, [callsData]);

  // Helper function to map SMS status - MUST be declared before sms useMemo
  const mapSmsStatus = (status: string): 'sent' | 'delivered' | 'failed' | 'pending' | 'received' => {
    const statusMap: Record<string, 'sent' | 'delivered' | 'failed' | 'pending' | 'received'> = {
      'sent': 'sent',
      'delivered': 'delivered',
      'failed': 'failed',
      'undelivered': 'failed',
      'canceled': 'failed',
      'pending': 'pending',
      'queued': 'pending',
      'sending': 'pending',
      'accepted': 'pending',
      'scheduled': 'pending',
      // Inbound replies land as 'received'/'receiving' — give them their own
      // state so they don't fall through to the 'pending' default and show a
      // wrong "Pending" badge on messages that were actually received.
      'received': 'received',
      'receiving': 'received',
      'read': 'received',
    };
    return statusMap[status?.toLowerCase()] || 'pending';
  };

  // Extract and transform SMS data
  const sms = useMemo(() => {
    if (!smsData?.items || !Array.isArray(smsData.items)) {
      return [];
    }
    // Transform backend SMS data to frontend format
    return smsData.items.map((item: any) => ({
      id: item.id,
      phoneNumber: item.phoneNumber,
      contactName: item.contactName || null,
      leadId: item.leadId ?? null,
      direction: item.messageType === 'inbound' ? 'inbound' : 'outbound',
      message: item.messageContent || '',
      status: mapSmsStatus(item.messageStatus),
      timestamp: item.messageDate ? new Date(item.messageDate) : new Date(item.createdAt),
      errorCode: item.errorCode ?? null,
      errorMessage: item.errorMessage ?? null,
    }));
  }, [smsData]);

  // Extract Email data
  const emails: EmailHistory[] = useMemo(() => {
    if (!emailsData?.items || !Array.isArray(emailsData.items)) {
      return [];
    }
    return emailsData.items as EmailHistory[];
  }, [emailsData]);

  // Check if OAuth is configured for emails — default false until data loads
  const isOAuthConfigured = useMemo(() => {
    if (!emailsData) return false;
    return emailsData?.meta?.oauthConfigured !== false;
  }, [emailsData]);

  // Extract voicemails data and transform callType to direction
  const voicemails = useMemo(() => {
    if (!voicemailsData?.items || !Array.isArray(voicemailsData.items)) {
      return [];
    }
    // Transform backend data to VoicemailRecord format
    // Backend returns callType: 'incoming'/'outgoing', frontend expects direction: 'inbound'/'outbound'
    return voicemailsData.items.map((item: any) => ({
      id: item.id,
      leadId: item.leadId,
      phoneNumber: item.phoneNumber,
      contactName: item.contactName,
      // Map callType to direction: 'incoming' -> 'inbound', 'outgoing' -> 'outbound'
      direction: (item.callType === 'incoming' || item.direction === 'inbound') ? 'inbound' : 'outbound',
      duration: item.duration || 0,
      audioUrl: getProxyRecordingUrl(item.recordingUrl) || getProxyRecordingUrl(item.audioUrl) || '',
      transcript: item.notes || item.transcript,
      isListened: item.isListened || false,
      timestamp: item.callDate ? new Date(item.callDate) : item.timestamp ? new Date(item.timestamp) : item.createdAt ? new Date(item.createdAt) : new Date(),
      callId: item.callSid || item.callId,
      notes: item.notes,
      priority: item.priority || 'normal',
    })) as VoicemailRecord[];
  }, [voicemailsData]);

  // Count unread voicemails
  const unreadVoicemailCount = useMemo(() => {
    return voicemails.filter(v => !v.isListened && v.direction === "inbound").length;
  }, [voicemails]);

  // Get leads data for voicemail functionality with higher limit.
  // lite: this only ever reads id / name / phone (see the map below), so there is no
  // reason to pull policies, comments and beneficiaries for 1,000 leads — doing so is
  // what put 6 GB on the backend heap and aborted the process on 2026-08-18.
  const { data: leadsData } = useGetLeads({ page: 1, limit: 500, lite: 'true' });

  interface LeadData {
    id: string;
    name: string;
    phone?: string;
  }

  interface LeadsResponse {
    data?: {
      items?: LeadData[];
    };
  }

  const typedLeadsData = leadsData as LeadsResponse;
  const leads = typedLeadsData?.data?.items?.map((lead) => ({
    id: lead.id,
    phone: lead.phone || '',
    firstName: lead.name?.split(' ')[0] || '',
    lastName: lead.name?.split(' ').slice(1).join(' ') || ''
  })) || [];

  // Duration filter minimum seconds mapping
  const DURATION_SECONDS: Record<DurationFilter, number> = {
    all: 0,
    '30s': 30,
    '60s': 60,
    '2min': 120,
    '5min': 300,
  };

  // Client-side filtering for direction, status, and duration (backend doesn't support these filters)
  const filteredCalls = useMemo(() => {
    return calls.filter((call) => {
      // Search is already handled by backend, but we keep it here for consistency
      const matchesSearch =
        searchQuery === "" ||
        call.phoneNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        call.contactName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        call.notes?.toLowerCase().includes(searchQuery.toLowerCase());

      // Direction and status filters are now handled server-side
      // Only apply client-side if backend doesn't support the specific filter
      const matchesDirection = true; // Server-side filtered
      const matchesStatus = true; // Server-side filtered

      // Duration filter
      const matchesDuration =
        callDurationFilter === "all" || call.duration >= DURATION_SECONDS[callDurationFilter];

      return matchesSearch && matchesDirection && matchesStatus && matchesDuration;
    });
  }, [calls, searchQuery, callDirectionFilter, callStatusFilter, callDurationFilter]);

  // Filter SMS based on search, direction, and status
  const filteredSMS = useMemo(() => {
    return sms.filter((smsItem: any) => {
      const matchesSearch =
        searchQuery === "" ||
        smsItem.phoneNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        smsItem.contactName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        smsItem.message?.toLowerCase().includes(searchQuery.toLowerCase());

      // Direction and status filters are now handled server-side
      const matchesDirection = true;
      const matchesStatus = true;

      return matchesSearch && matchesDirection && matchesStatus;
    });
  }, [sms, searchQuery, smsDirectionFilter, smsStatusFilter]);

  // Handler functions to match the expected interface
  const handleCallStatusChange = (
    status:
      | "all"
      | "answered"
      | "missed"
      | "declined"
      | "failed"
      | "busy"
      | "voicemail"
      | "no-answer"
      | "canceled"
      | "sent"
      | "delivered"
      | "received"
      | "pending"
      | "queued"
      | "sending"
      | "undelivered"
      | "bounced"
      | "opened"
      | "clicked"
      | "unsubscribed"
  ) => {
    // Ignore email-only and SMS-only statuses
    if (status === "sent" || status === "delivered" || status === "received" ||
        status === "pending" || status === "queued" || status === "sending" ||
        status === "undelivered" || status === "bounced" || status === "opened" ||
        status === "clicked" || status === "unsubscribed") {
      return;
    }
    setCallStatusFilter(status as StatusFilter);
    setCallPage(1); // Reset to page 1 when filter changes
  };

  const handleSmsStatusChange = (
    status:
      | "all"
      | "answered"
      | "missed"
      | "declined"
      | "failed"
      | "busy"
      | "voicemail"
      | "no-answer"
      | "canceled"
      | "sent"
      | "delivered"
      | "received"
      | "pending"
      | "queued"
      | "sending"
      | "undelivered"
      | "bounced"
      | "opened"
      | "clicked"
      | "unsubscribed"
  ) => {
    // Ignore call-only statuses
    if (
      status === "answered" ||
      status === "missed" ||
      status === "declined" ||
      status === "busy" ||
      status === "voicemail" ||
      status === "no-answer"
    ) {
      return;
    }
    setSmsStatusFilter(status as SMSStatusFilter);
    setSmsPage(1); // Reset to page 1 when filter changes
  };

  const handleEmailStatusChange = (status:
    | "all"
    | "pending"
    | "failed"
    | "answered"
    | "missed"
    | "declined"
    | "busy"
    | "voicemail"
    | "no-answer"
    | "canceled"
    | "queued"
    | "sending"
    | "sent"
    | "delivered"
    | "received"
    | "undelivered"
    | "bounced"
    | "opened"
    | "clicked"
    | "unsubscribed"
  ) => {
    // Ignore call-only and SMS-only statuses
    if (
      status === "answered" ||
      status === "missed" ||
      status === "declined" ||
      status === "busy" ||
      status === "voicemail" ||
      status === "no-answer" ||
      status === "canceled" ||
      status === "undelivered"
    ) {
      return;
    }
    // Map "pending" to "sending" for email status
    const mappedStatus = status === "pending" ? "sending" : status;
    setEmailStatusFilter(mappedStatus as EmailStatusFilter);
  };

  // Voicemail handlers
  const handleMarkAsListened = async (voicemailId: string) => {
    try {
      await markAsListenedMutation.mutateAsync(voicemailId);
      toast({
        title: "Voicemail marked as listened",
        description: "The voicemail has been marked as listened.",
      });
    } catch (error) {
      toast({
        title: "Failed to mark as listened",
        description: "An error occurred while marking the voicemail as listened.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteVoicemail = async (voicemailId: string) => {
    try {
      await deleteVoicemailMutation.mutateAsync(voicemailId);
      toast({
        title: "Voicemail deleted",
        description: "The voicemail has been successfully deleted.",
      });
    } catch (error) {
      toast({
        title: "Failed to delete voicemail",
        description: "An error occurred while deleting the voicemail.",
        variant: "destructive",
      });
    }
  };

  const handleAddVoicemail = (newVoicemail: VoicemailRecord) => {
    // Will be implemented when voicemail sending is added
    toast({
      title: "Voicemail sent",
      description: "Your voicemail has been sent successfully.",
    });
  };

  return (
    <div className="space-y-6 overflow-visible">
      <div data-tour="communication-header" className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">
          Communication Center
        </h1>
      </div>

      {/* Main tabs */}
      <Tabs className="w-full overflow-visible" value={activeTab} onValueChange={handleTabChange}>
        <TabsList data-tour="communication-tabs" className="communication-tabs-list flex flex-row flex-nowrap w-full overflow-visible" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap' }}>
          <TabsTrigger data-tour="communication-tab-calls" className="flex-1 flex items-center justify-center gap-1 px-2 text-xs sm:text-sm sm:gap-2 sm:px-3 min-w-0" value="call-history" style={{ flex: '1 1 0%' }}>
            <Phone className="h-4 w-4 text-primary shrink-0" />
            <span className="truncate">Calls</span>
          </TabsTrigger>
          <TabsTrigger data-tour="communication-tab-sms" className="flex-1 flex items-center justify-center gap-1 px-2 text-xs sm:text-sm sm:gap-2 sm:px-3 min-w-0" value="sms-history" style={{ flex: '1 1 0%' }}>
            <MessageSquare className="h-4 w-4 text-primary shrink-0" />
            <span className="truncate">SMS</span>
          </TabsTrigger>
          <TabsTrigger data-tour="communication-tab-emails" className="flex-1 flex items-center justify-center gap-1 px-2 text-xs sm:text-sm sm:gap-2 sm:px-3 min-w-0" value="email-history" style={{ flex: '1 1 0%' }}>
            <Mail className="h-4 w-4 text-primary shrink-0" />
            <span className="truncate">Emails</span>
          </TabsTrigger>
          <TabsTrigger data-tour="communication-tab-voicemail" className="flex-1 flex items-center justify-center gap-1 px-2 text-xs sm:text-sm sm:gap-2 sm:px-3 min-w-0" value="voicemail" style={{ flex: '1 1 0%' }}>
            <Mic className="h-4 w-4 text-primary shrink-0" />
            <span className="truncate">Voicemail</span>
            {unreadVoicemailCount > 0 && (
              <span className="bg-red-500 text-white text-xs rounded-full h-5 min-w-5 px-1 flex items-center justify-center ml-1 shrink-0">
                {unreadVoicemailCount > 99 ? "99+" : unreadVoicemailCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent className="space-y-4" value="call-history">
          <div className="bg-card rounded-lg shadow-sm border p-6">
            <CommunicationFilters
              data-tour="communication-filters"
              directionFilter={callDirectionFilter}
              onDirectionChange={setCallDirectionFilter}
              onSearchChange={setSearchQuery}
              onStatusChange={handleCallStatusChange}
              searchQuery={searchQuery}
              statusFilter={callStatusFilter}
              type="calls"
              callDurationFilter={callDurationFilter}
              onDurationChange={setCallDurationFilter}
            />

            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-foreground">
                  Recent Calls ({filteredCalls.length})
                  {isLoadingCalls && <span className="text-sm text-muted-foreground ml-2">Loading...</span>}
                </h3>
                {callsError && (
                  <button
                    onClick={() => refetchCalls()}
                    className="text-sm text-primary hover:text-primary/80"
                  >
                    Retry
                  </button>
                )}
              </div>

              {callsError ? (
                <div className="text-center py-12 text-red-600">
                  {callsError instanceof Error ? callsError.message : String(callsError)}
                </div>
              ) : (
                <>
                  <CallList calls={filteredCalls} />
                  {callsData?.meta && (
                    <DataPagination
                      meta={callsData.meta}
                      onPageChange={setCallPage}
                      onPageSizeChange={setCallPageSize}
                      isLoading={isLoadingCalls}
                      showPageSize={true}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent className="space-y-4" value="sms-history">
          <div className="bg-card rounded-lg shadow-sm border p-6">
            <CommunicationFilters
              directionFilter={smsDirectionFilter}
              onDirectionChange={setSmsDirectionFilter}
              onSearchChange={setSearchQuery}
              onStatusChange={handleSmsStatusChange}
              searchQuery={searchQuery}
              statusFilter={smsStatusFilter}
              type="sms"
            />

            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-foreground">
                  Recent Messages ({smsData?.meta?.totalItems ?? filteredSMS.length})
                  {isLoadingSms && <span className="text-sm text-muted-foreground ml-2">Loading...</span>}
                </h3>
                {smsError && (
                  <button
                    onClick={() => refetchSMS()}
                    className="text-sm text-primary hover:text-primary/80"
                  >
                    Retry
                  </button>
                )}
              </div>

              {smsError ? (
                <div className="text-center py-12 text-red-600">
                  {smsError instanceof Error ? smsError.message : String(smsError)}
                </div>
              ) : (
                <>
                  <SMSList smsRecords={filteredSMS} />
                  {smsData?.meta && (
                    <DataPagination
                      meta={smsData.meta}
                      onPageChange={setSmsPage}
                      onPageSizeChange={setSmsPageSize}
                      isLoading={isLoadingSms}
                      showPageSize={true}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent className="space-y-4" value="email-history">
          <div className="bg-card rounded-lg shadow-sm border p-6">
            <CommunicationFilters
              directionFilter={emailDirectionFilter}
              onDirectionChange={setEmailDirectionFilter}
              onSearchChange={setSearchQuery}
              onStatusChange={handleEmailStatusChange}
              searchQuery={searchQuery}
              statusFilter={emailStatusFilter}
              type="emails"
            />

            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-foreground">
                  Recent Emails ({emails.length})
                  {isLoadingEmails && <span className="text-sm text-muted-foreground ml-2">Loading...</span>}
                </h3>
                {emailsError && (
                  <button
                    onClick={() => refetchEmails()}
                    className="text-sm text-primary hover:text-primary/80"
                  >
                    Retry
                  </button>
                )}
              </div>

              {!isOAuthConfigured ? (
                <div className="text-center py-12">
                  <Mail className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Email integration not configured
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Connect your Gmail or Outlook account to view email history
                  </p>
                  <Button
                    onClick={() => router.push(
                      // Shell-aware: this page is also embedded at /m/communication
                      window.location.pathname.startsWith('/m')
                        ? '/m/settings?tab=account'
                        : '/settings?tab=account',
                    )}
                    variant="default"
                  >
                    Connect Email
                  </Button>
                </div>
              ) : emailsError ? (
                <div className="text-center py-12 text-red-600">
                  {emailsError instanceof Error ? emailsError.message : String(emailsError)}
                </div>
              ) : (
                <EmailList emails={emails} onEmailClick={handleEmailClick} />
              )}
            </div>
          </div>
        </TabsContent>

            <TabsContent className="space-y-4" value="voicemail">
              <VoicemailTab
                leads={leads}
                onAddVoicemail={handleAddVoicemail}
                onDeleteVoicemail={handleDeleteVoicemail}
                onMarkAsListened={handleMarkAsListened}
                voicemails={voicemails}
              />
            </TabsContent>
      </Tabs>

      {/* Email Detail Modal */}
      <EmailDetailModal
        open={isEmailDetailOpen}
        onOpenChange={setIsEmailDetailOpen}
        email={selectedEmail}
        leadId={selectedEmail?.leadId}
      />
    </div>
  );
};

export default Communication;
